import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  CheckCircleOutline,
  CancelOutlined,
  EditOutlined,
  DoNotDisturbAltOutlined,
} from '@mui/icons-material';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';
import CrearPromesaPagoDialog from './crearpromesapagodialog';
import { ESTADO_LABELS, ESTADO_COLORS, computedEstado } from './mockdata';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

const formatFecha = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d)) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

export default function PromesasTab({ prestamo }) {
  const [promesas, setPromesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const [menuPromesa, setMenuPromesa] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const defaultPrestamo = useMemo(
    () =>
      prestamo?.id
        ? {
            loan_id: prestamo.loan_id,
            prestamo_original_id: prestamo.id,
            cliente_nombre: prestamo.cliente_nombre,
            cliente_identificacion: prestamo.cliente_identificacion,
          }
        : null,
    [prestamo],
  );

  const fetchPromesas = useCallback(async () => {
    if (!prestamo?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/promesas_pago/prestamo/${prestamo.id}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      setPromesas(Array.isArray(data) ? data : []);
    } catch (err) {
      setPromesas([]);
    } finally {
      setLoading(false);
    }
  }, [prestamo?.id]);

  useEffect(() => { fetchPromesas(); }, [fetchPromesas]);

  const promesasConEstado = useMemo(
    () => promesas.map((p) => ({
      ...p,
      monto_prometido: parseFloat(p.monto_prometido) || 0,
      monto_pagado: parseFloat(p.monto_pagado) || 0,
      estadoComputed: computedEstado(p),
    })),
    [promesas],
  );

  const openMenu = (event, promesa) => {
    setMenuEl(event.currentTarget);
    setMenuPromesa(promesa);
  };

  const closeMenu = () => {
    setMenuEl(null);
    setMenuPromesa(null);
  };

  const handleMarcarEstado = async (estado) => {
    if (!menuPromesa) return;
    try {
      const body = { estado };
      if (estado === 'cumplida') body.monto_pagado = menuPromesa.monto_prometido;
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/promesas_pago/${menuPromesa.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error al actualizar la promesa');
      setAlert({ open: true, message: `Promesa marcada como ${ESTADO_LABELS[estado].toLowerCase()}`, severity: 'success' });
      await fetchPromesas();
    } catch (err) {
      setAlert({ open: true, message: err.message, severity: 'error' });
    } finally {
      closeMenu();
    }
  };

  const handleEdit = () => {
    setEditing(menuPromesa);
    setDialogOpen(true);
    closeMenu();
  };

  const handleSave = async () => {
    setAlert({ open: true, message: editing ? 'Promesa actualizada' : 'Promesa registrada', severity: 'success' });
    setEditing(null);
    await fetchPromesas();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primarybutton"
          size="small"
          startIcon={<Add />}
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          sx={{ fontWeight: 700, textTransform: 'none' }}
        >
          Nueva promesa
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledCellHeader sx={{ textAlign: 'center' }}>Fecha</StyledCellHeader>
              <StyledCellHeader sx={{ textAlign: 'right' }}>Monto</StyledCellHeader>
              <StyledCellHeader sx={{ textAlign: 'right' }}>Pagado</StyledCellHeader>
              <StyledCellHeader sx={{ textAlign: 'center' }}>Estado</StyledCellHeader>
              <StyledCellHeader>Notas</StyledCellHeader>
              <StyledCellHeader>Registrada por</StyledCellHeader>
              <StyledCellHeader sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  Cargando promesas…
                </TableCell>
              </TableRow>
            )}
            {!loading && promesasConEstado.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  Este préstamo no tiene promesas registradas.
                </TableCell>
              </TableRow>
            )}
            {promesasConEstado.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ textAlign: 'center' }}>{formatFecha(p.fecha_promesa)}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>RD${formatNumberWithCommas(p.monto_prometido.toFixed(2))}</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>RD${formatNumberWithCommas(p.monto_pagado.toFixed(2))}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <Chip size="small" label={ESTADO_LABELS[p.estadoComputed]} color={ESTADO_COLORS[p.estadoComputed]} sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.notas || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{p.created_by_nombre || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{formatFecha(p.created_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="Acciones">
                    <IconButton size="small" onClick={(e) => openMenu(e, p)}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
      >
        <MenuItem onClick={handleEdit}>
          <EditOutlined sx={{ mr: 1.5, fontSize: 18, color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={600}>Editar</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMarcarEstado('cumplida')}>
          <CheckCircleOutline sx={{ mr: 1.5, fontSize: 18, color: 'success.main' }} />
          <Typography variant="body2" fontWeight={600}>Marcar cumplida</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMarcarEstado('incumplida')}>
          <CancelOutlined sx={{ mr: 1.5, fontSize: 18, color: 'error.main' }} />
          <Typography variant="body2" fontWeight={600}>Marcar incumplida</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleMarcarEstado('cancelada')}>
          <DoNotDisturbAltOutlined sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight={600}>Cancelar promesa</Typography>
        </MenuItem>
      </Menu>

      <CrearPromesaPagoDialog
        open={dialogOpen}
        initial={editing}
        defaultPrestamo={defaultPrestamo}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={handleSave}
      />

      <AlertMessageDialog
        open={alert.open}
        handleClose={() => setAlert({ ...alert, open: false })}
        title=""
        message={alert.message}
        severity={alert.severity}
        color={alert.severity}
      />
    </Box>
  );
}
